<template>
  <div class="container">
    <RefreshResults
      v-if="showRefreshNotification"
      :type="'hotel'"
      @clearResults="resetView"
      @refreshSearch="rerunSearch"
      :allowRefresh="
        recentSearches.findIndex((search) => search.key === cacheKey) > -1
      "
    />
    <div v-else>
      <div v-if="searchResults.length > 0">
        <div v-if="filteredResults">
          <div id="selectedFilters">
            <b-tag
              v-for="(filter, index) in selectedFilters"
              :key="index"
              :type="'is-success'"
              closable
              @close="removeFilter(filter)"
              class="mr-2 mb-2"
            >
              {{ filter.value }}
            </b-tag>
          </div>

          <div
            v-if="!(filteredResults.length > 0) && !loading"
            class="has-text-centered"
          >
            <b-message
              type="is-danger"
              has-icon
              closable
              @close="resetFilters"
              title="No results found"
            >
              <span class="has-text-weight-bold mr-2">No results found.</span>
              <span>Remove some filters to see more results.</span>
            </b-message>
          </div>
        </div>

        <div class="pt-4" data-id="hotel_results_list">
          <div
            v-for="result in filteredResults"
            :key="result.hotelCode"
            class="h-result"
            :class="{ 'is-grayed-out': roomsCount(result) == 0 }"
            data-test="h_result"
          >
            <Result
              :result="result"
              :preferred-vendors="preferredVendors"
              :autoload-price="false"
              :selected="selectedHotelCode === result.hotelCode"
              :cacheKey="cacheKey"
              @setLiveData="setLiveData"
            />
          </div>
        </div>
      </div>

      <div v-else-if="loading" class="pt-4">
        <div v-if="loading" class="has-text-centered mb-6">
          <i class="fas fa-gear has-text-primary fa-3x fa-spin"></i>
        </div>

        <ResultSkeleton v-for="n in 3" :key="n" />
      </div>

      <b-message
        v-else
        type="is-danger"
        has-icon
        class="mx-auto mt-5"
        style="border: 1px red solid"
      >
        <div class="has-text-weight-bold is-size-5">
          We're sorry, but we were unable to find hotel results matching your
          search.
        </div>
        <div class="is-size-6">
          Please check your location and dates, and try your search again.
        </div>
        <div
          v-if="searchErrors.length > 0"
          v-html="searchErrors[0].title"
          class="pt-4 mb-2"
        ></div>
      </b-message>

      <b-message
        v-if="additionalItems.length > 0"
        class="additional-items-message"
        type="is-info is-light"
      >
        We have {{ additionalItems.length }} more hotels for your search. <br />
        <a @click="expandResults">See full results</a>
      </b-message>
    </div>
  </div>
</template>

<script>
import Result from "./Result.vue";
import ResultSkeleton from "./ResultSkeleton.vue";
import RefreshResults from "@/components/BookTravel/RefreshResults.vue";
import { mapState, mapGetters } from "vuex";

export default {
  components: {
    Result,
    ResultSkeleton,
    RefreshResults,
  },

  props: {
    cacheKey: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      refreshDialogComp: null,
      selectedHotelCode: null,
      additionalItems: [],
      refreshTimer: null,
    };
  },

  computed: {
    ...mapState({
      searchErrors: (state) => state.hotelSearch.searchErrors,
      searchResults: (state) => state.hotelSearch.searchResults,
      sortOrder: (state) => state.hotelSearch.sortOrder,
      profile: (state) => state.travellersAndTrips.currentTravellerProfile,
      filters: (state) => state.hotelSearch.filters,
      recentSearches: (state) => state.hotelSearch.recentSearches,
      loading: (state) => state.hotelSearch.resultsLoading,
      showRefreshNotification: (state) =>
        state.hotelSearch.showRefreshNotification,
    }),
    ...mapGetters({
      filteredResults: "hotelSearch/filteredResults",
      fullAmenities: "hotelSearch/getFullAmenities",
      availableChains: "hotelSearch/getAvailableChains",
      availablePolicies: "hotelSearch/getPolicies",
      preferredChains: "hotelSearch/getPreferredChains",
    }),

    showResults: {
      get() {
        return this.$store.state.hotelSearch.showResults;
      },
      set(value) {
        this.$store.commit("hotelSearch/SET_SHOW_RESULTS", value);
      },
    },

    priceRange() {
      return this.getPriceRange(this.filterItems("price"));
    },

    priceData() {
      return this.searchResults.map((x) => parseInt(x.cheapestPrice));
    },

    selectedFilters() {
      const selected = [];
      // Filter logic implementation
      return selected;
    },

    preferredVendors() {
      return this.profile?.preferredHotelVendors || [];
    },
  },

  watch: {
    cacheKey(newKey, oldKey) {
      if (newKey !== oldKey) {
        console.log("key: " + newKey);

        this.loadSavedSearch(newKey);
      }
    },
  },

  mounted() {},

  beforeDestroy() {
    if (this.refreshDialogComp) {
      this.refreshDialogComp.close();
    }
    clearTimeout(this.refreshTimer);
  },

  methods: {
    removeFilter(filter) {
      // Filter removal logic
    },

    resetView() {
      this.$store.commit("hotelSearch/RESET_FILTERS");
      this.$store.commit("hotelSearch/SET_REFRESH_NOTIFICATION", false);
      this.showResults = false;

      this.$router.push({
        path: "hotels",
      });
    },

    rerunSearch() {
      this.$buefy.toast.open(`Refreshing hotel search!`);
      //clearTimeout(this.refreshTimer);

      const priorSearch = this.$store.state.hotelSearch.recentSearches.find(
        (search) => search.key === this.cacheKey
      );
      if (priorSearch) {
        this.$store.commit("hotelSearch/SET_FORM_DATA", priorSearch.formData);
        this.getData();
      }
    },

    //Convert String date formated ("2025-05-22") into Date object w/o timezone
    convertDate(dateStr) {
      // Split the date string into parts
      const [year, month, day] = dateStr.split("-").map(Number);

      // Month is 0-based in JavaScript Date constructor, so subtract 1
      return new Date(year, month - 1, day);
    },

    loadSavedSearch(cacheKey) {
      console.log("loading saved search for key: " + cacheKey);

      let savedSearch = this.recentSearches.find(
        (search) => search.key === cacheKey
      );

      if (savedSearch) {
        //clone the saved search data
        let savedData = JSON.parse(JSON.stringify(savedSearch.formData));

        console.log("savedData", savedData);
        const parsedDates = [
          this.convertDate(savedData.checkinDate),
          this.convertDate(savedData.checkoutDate),
        ];

        console.log(parsedDates);

        //pre-fill location and dates form values
        if (!this.pathMixin_isMeeting) {
          this.$store.commit("hotelSearch/SET_SEARCH_LOCATION", savedData.loc);
        }

        this.$store.commit("hotelSearch/SET_SEARCH_DATES", parsedDates);

        //this.$store.dispatch("hotelSearch/setDatesFromText", [
        //  savedData.checkinDate,
        //  savedData.checkoutDate,
        //])

        //retrieve search results from cache
        this.getData(cacheKey);
      }
    },

    sort() {
      this.selectedHotelCode = null; // Reset selected hotel before sorting

      // Dispatch sort through Vuex mutation
      this.$store.commit("hotelSearch/SORT_RESULTS", this.sortOrder);
    },

    resetFilters() {
      //To Do: Add Toast Message
      this.$store.commit("hotelSearch/RESET_FILTERS");
    },

    async getData(cacheKey = null) {
      let result = await this.$store.dispatch("hotelSearch/getData", {
        cacheKey: cacheKey,
      });
    },

    roomsCount(hotel) {
      if (hotel.rooms) {
        return hotel.rooms?.length;
      } else {
        return null;
      }
    },

    setLiveData(payload) {
      // Implementation remains largely the same
    },
  },
};
</script>

<style>
.is-grayed-out .property_image {
  opacity: 0.6;
}

.is-grayed-out .property_name {
  opacity: 0.6;
}

.additional-items-message {
  position: fixed;
  width: 275px;
  font-size: 12px;
  right: 50px !important;
  top: 70px !important;
  padding: 0;
  z-index: 100;
  text-align: center;
}
</style>
